import { PayPalOptions } from './payment-methods/paypal/types';
import { PaymentFlow, ThreeDSecureStatus } from './enums/Tokenization';
import {
  TokenType,
  PaymentInstrumentType,
  PaymentMethodType,
} from './enums/Tokens';
import { GooglePayOptions } from './payment-methods/google-pay/types';
import { ApplePayOptions } from './payment-methods/apple-pay/types';
import { DirectDebitOptions } from './payment-methods/go-cardless/types';
import { ErrorCode, PrimerClientError } from './errors';
import { CheckoutStyle } from './styles';
import { MaybeThunk, Nullable } from './utilities';
import { CheckoutUXFlow } from './enums/Checkout';
import { CardMetadata } from './hosted-scripts/CardMetadata';

export type KlarnaPaymentCategoryType =
  | 'pay_now'
  | 'pay_later'
  | 'pay_over_time';

export type KlarnaSupportedLocale =
  | 'de-AT'
  | 'en-AT'
  | 'da-DK'
  | 'en-DK'
  | 'fi-FI'
  | 'sv-FI'
  | 'en-FI'
  | 'de-DE'
  | 'en-DE'
  | 'nl-NL'
  | 'en-NL'
  | 'nb-NO'
  | 'en-NO'
  | 'sv-SE'
  | 'en-SE'
  | 'de-CH'
  | 'fr-CH'
  | 'it-CH'
  | 'en-CH'
  | 'en-GB'
  | 'en-US'
  | 'en-AU'
  | 'nl-BE'
  | 'fr-BE'
  | 'es-ES'
  | 'it-IT';

export interface KlarnaAddress {
  title?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  postalCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  country?: Alpha2CountryCode;
  city?: string;
  email?: string;
}

export interface KlarnaCustomerDetails {
  dateOfBirth?: string;
  gender?: 'MALE' | 'FEMALE';
  nationalIdentificationNumber?: string;
  title?: string;
}

export interface KlarnaOptions {
  paymentFlow?: PaymentFlow;
  recurringPaymentDescription?: string;
  locale?: KlarnaSupportedLocale;
  allowedPaymentCategories?: KlarnaPaymentCategoryType[];
  orderDetails?: OrderDetails;
  customer?: KlarnaCustomerDetails;
  billingAddress?: KlarnaAddress;
  shippingAddress?: KlarnaAddress;
}

export {
  TokenType,
  PaymentInstrumentType,
  PaymentMethodType,
  CheckoutUXFlow,
  PaymentFlow,
  ThreeDSecureStatus,
  ErrorCode,
};

export type SupportedLocale =
  | 'de'
  | 'de-DE'
  | 'en'
  | 'en-GB'
  | 'es'
  | 'es-ES'
  | 'fr'
  | 'fr-FR'
  | 'it'
  | 'it-IT';

export type Alpha2CountryCode = 'ES' | 'FR' | 'GB' | 'DE' | 'PL' | 'IT';

export type Alpha3CurrencyCode = 'EUR' | 'GBP' | 'USD';

export type ElementOrID = Nullable<string | HTMLElement>;

export type ValidationErrorType = 'required' | 'invalid';

export type Label<
  T extends Record<string, unknown> = Record<string, unknown>
> = string | ((options: { locale: SupportedLocale } | T) => string);

export interface ClientContextOptions {
  credentials: PrimerClientCredentials;
  thirdPartyScriptTimeout?: number;
}

export type ResumeToken = {
  resumeToken: string;
};

export interface InputValidationError {
  name: string;
  error: string;
  message: string;
}

export interface Validation {
  valid: boolean;
  validationErrors: InputValidationError[];
  error?: string;
}
export interface InputMetadata {
  errorCode: string | null;
  error: string | null;
  valid: boolean;
  active: boolean;
  dirty: boolean;
  touched: boolean;
  submitted: boolean;
}

export interface FormState {
  dirty: boolean;
  touched: boolean;
  active: boolean;
  valid: boolean;
  submitted: boolean;
}

export type InlineStyleMap = Partial<Record<keyof CSSStyleDeclaration, string>>;

export interface PrimerClientCredentials {
  clientToken: string;
}
export interface PrimerClientOptions {
  credentials: PrimerClientCredentials;
}

export interface ExternalPayerInfo {
  externalPayerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface BillingAddress {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  city: string;
  state?: string;
  countryCode: Alpha2CountryCode;
  postalCode: string;
}

export interface CustomerDetails {
  customerTaxId?: string;
  shippingAddress?: BillingAddress;
  billingAddress?: BillingAddress;
}

export interface BusinessDetails {
  address?: BillingAddress;
  nexusAddresses?: [BillingAddress];
}

export interface MonetaryAmount {
  value: number | string;
  currency: Alpha3CurrencyCode;
}

export interface OrderItem {
  name: string;
  unitAmount: number | string;
  reference?: string;
  quantity?: number;
  discountAmount?: number | string;
  taxCode?: string;
}

export interface OrderDetails {
  totalAmount?: number | string;
  totalTaxAmount?: number | string;
  currencyCode?: Alpha3CurrencyCode;
  countryCode?: Alpha2CountryCode;
  items?: OrderItem[];
  shippingAmount?: number | string;
  taxExemptionType?: string;
}

export interface ThreeDSecureOrderDetails {
  amount: MonetaryAmount;
  email: string;
  billingAddress: BillingAddress;
  orderId: string;
}

export interface ThreeDSVerificationOptions {
  token: string;
  container: string;
  order: ThreeDSecureOrderDetails;
  testScenario?: string;
  onChallengeStart?: () => void;
  onChallengeEnd?: () => void;
}

export interface ThreeDSAuthenticationData {
  responseCode: ThreeDSecureStatus;
  reasonCode?: string;
  reasonText?: string;
  protocolVersion: string;
  challengeIssued: boolean;
}

export interface VaultData {
  customerId: string;
}

export interface PaymentCardDetails {
  last4Digits: string;
  cardholderName: string;
  network: string;
}

export interface PayPalBillingAgreementDetails {
  paypalBillingAgreementId: string;
  externalPayerInfo?: ExternalPayerInfo;
  shippingAddress?: BillingAddress;
}

export interface GoCardlessDetails {
  gocardlessMandateId: string;
}

export interface IPaymentMethodToken<T, U extends PaymentInstrumentType> {
  token: string;
  analyticsId: string;
  tokenType: TokenType;
  paymentInstrumentData: T;
  paymentInstrumentType: U;
  threeDSecureAuthentication: Nullable<ThreeDSAuthenticationData>;
  vaultData: Nullable<VaultData>;
}

export type BaseAmountChange = {
  totalAmount: number;
};

export type TaxAmountChange = {
  reason: 'TAX';
  totalTaxAmount: number;
};

export type AmountChange = BaseAmountChange & TaxAmountChange;

export type PaymentCardToken = IPaymentMethodToken<
  PaymentCardDetails,
  PaymentInstrumentType.CARD
>;

export type PayPalBillingAgreementToken = IPaymentMethodToken<
  PayPalBillingAgreementDetails,
  PaymentInstrumentType.PAYPAL_VAULTED
>;

export type GoCardlessToken = IPaymentMethodToken<
  GoCardlessDetails,
  PaymentInstrumentType.GO_CARDLESS
>;

export type PaymentMethodToken =
  | PaymentCardToken
  | PayPalBillingAgreementToken
  | GoCardlessToken;

export interface ThreeDSVerificationResult<T extends ThreeDSecureStatus> {
  status: T;
  error: Nullable<PrimerClientError>;
  data: PaymentMethodToken;
  resumeToken: string;
}

export type ThreeDSVerification =
  | ThreeDSVerificationResult<ThreeDSecureStatus.SUCCESS>
  | ThreeDSVerificationResult<ThreeDSecureStatus.FAILED>
  | ThreeDSVerificationResult<ThreeDSecureStatus.SKIPPED>;

export interface PurchaseInformation {
  totalAmount: MonetaryAmount;
}

export interface VaultInformation {
  totalAmount: {
    currency: Alpha3CurrencyCode;
  };
}

export interface VaultDetails {
  currencyCode: Alpha3CurrencyCode;
}

export interface CheckoutCardOptions {
  css?: string;
  /**
   * @deprecated Use `cardholderName.required` instead
   */
  cardholderNameRequired?: boolean;
  vault?: MaybeThunk<boolean>;

  cardholderName?: {
    required?: boolean;
    visible?: boolean;
    placeholder?: Label;
  };

  cardNumber?: {
    placeholder?: Label;
  };

  expiryDate?: {
    placeholder?: Label;
  };

  cvv?: {
    placeholder?: Label;
  };
}

export interface TransitionDurationOptions {
  enter?: number;
  exit?: number;
}

export interface SubmitButtonOptions {
  visible?: boolean;
}

export interface ProcessingIndicatorOptions {
  visible?: boolean;
}

export interface FormOptions {
  inputLabelsVisible?: boolean;
}

export type CardNetwork =
  | 'american-express'
  | 'diners-club'
  | 'discover'
  | 'elo'
  | 'hiper'
  | 'hipercard'
  | 'interac'
  | 'jcb'
  | 'maestro'
  | 'mastercard'
  | 'mir'
  | 'unionpay'
  | 'visa';

export interface ErrorMessageOptions {
  disabled?: boolean;
  onErrorMessageShow?: (message: string) => void;
  onErrorMessageHide?: () => void;
}

export enum SuccessScreenType {
  /**
   * @deprecated Use `PAYMENT_METHOD` instead
   */
  PaymentMethod = 'PaymentMethod',

  PAYMENT_METHOD = 'PAYMENT_METHOD',

  CHECK = 'CHECK',
}

export type CheckSuccessScreenOptions = {
  type: SuccessScreenType.CHECK;
  title: Label;
};

export type PaymentMethodSuccessScreenOptions = {
  type: SuccessScreenType.PAYMENT_METHOD;
};

export type SuccessScreenOptions =
  | /* No success screen will be displayed */ false
  | /* Show the default success screen of the payment method*/ undefined
  // Proposed success screens
  | CheckSuccessScreenOptions
  | PaymentMethodSuccessScreenOptions;

export type SuccessCallbackReturnType =
  | /* Show success screen (for backward compatibility) */ undefined
  | /* Show success screen */ true
  | /* Refresh client token and perform new step */ { clientToken: string };

export type VaultOptions = {
  visible?: boolean;
  deletionDisabled?: boolean;
};

export interface SinglePaymentMethodCheckoutOptions {
  countryCode?: Alpha2CountryCode;
  /**
   * @deprecated Use `orderDetails` instead
   */
  purchaseInfo?: PurchaseInformation;
  uxFlow: CheckoutUXFlow.SINGLE_PAYMENT_METHOD_CHECKOUT;
  container: string | Element;
  paymentMethod: PaymentMethodType.GO_CARDLESS; // ONLY WORKS FOR GOCARDLESS
  locale?: SupportedLocale;
  customerId?: string;
  allowedCardNetworks?: CardNetwork[];
  onTokenizeStart?: () => void;
  onTokenizeSuccess: (
    data: PaymentMethodToken,
  ) => void | Promise<SuccessCallbackReturnType>;
  onTokenizeError?: (error: PrimerClientError) => void;
  onTokenizeProgress?: (evt: { type: string }) => void;
  onResumeSuccess: (data: ResumeToken) => Promise<SuccessCallbackReturnType>;
  onResumeError?: (error: PrimerClientError) => void;

  onAmountChange?: (data: AmountChange) => void;
  onAmountChanging?: (isChanging: boolean) => void;
  onAmountChangeError?: (error: PrimerClientError) => void;

  threeDSecure?: ThreeDSVerificationOptions;
  card?: CheckoutCardOptions;
  paypal?: Omit<PayPalOptions, 'container'>;
  googlePay?: Omit<GooglePayOptions, 'container'>;
  applePay?: Omit<ApplePayOptions, 'container'>;
  klarna?: KlarnaOptions;
  directDebit?: DirectDebitOptions;
  submitButton?: SubmitButtonOptions;
  processingIndicator?: ProcessingIndicatorOptions;
  style?: CheckoutStyle;
  errorMessage?: ErrorMessageOptions;
  successScreen?: SuccessScreenOptions;
  form?: FormOptions;
  customerDetails?: CustomerDetails;
  businessDetails?: BusinessDetails;
  orderDetails?: OrderDetails;
}

export interface VaultManagerOptions {
  uxFlow: CheckoutUXFlow.MANAGE_PAYMENT_METHODS;
  container: string | Element;
  locale?: SupportedLocale;
  card?: CheckoutCardOptions;
  allowedCardNetworks?: CardNetwork[];
  onTokenizeStart?: () => void;
  onTokenizeSuccess: (data: PaymentMethodToken) => void;
  onTokenizeError?: (message: PrimerClientError) => void;
  style?: CheckoutStyle;
  directDebit?: DirectDebitOptions;
  errorMessage?: ErrorMessageOptions;
  submitButton?: SubmitButtonOptions;
  threeDSecure?: ThreeDSVerificationOptions;
  countryCode?: Alpha2CountryCode;
  processingIndicator?: ProcessingIndicatorOptions;
  vaultOnly?: boolean;
  /**
   * @deprecated Use `orderDetails` instead
   */
  purchaseInfo?: VaultInformation;
  orderDetails?: VaultDetails;
  paypal?: Omit<PayPalOptions, 'container'>;
  deletionDisabled?: boolean;
  form?: FormOptions;
}

export interface CheckoutOptions {
  countryCode?: Alpha2CountryCode;
  /**
   * @deprecated Use `orderDetails` instead
   */
  purchaseInfo?: PurchaseInformation;
  uxFlow?: CheckoutUXFlow.CHECKOUT;
  container: string | Element;
  locale?: SupportedLocale;
  customerId?: string;
  allowedCardNetworks?: CardNetwork[];
  allowedPaymentMethods?: PaymentMethodType[];
  vault?: VaultOptions;
  onTokenizeStart?: () => void;
  onTokenizeSuccess: (
    data: PaymentMethodToken,
  ) => Promise<SuccessCallbackReturnType>;
  onTokenizeError?: (error: PrimerClientError) => void;
  onTokenizeProgress?: (evt: { type: string }) => void;

  onResumeSuccess: (data: ResumeToken) => Promise<SuccessCallbackReturnType>;
  onResumeError?: (error: PrimerClientError) => void;

  onAmountChange?: (data: AmountChange) => void;
  onAmountChanging?: (isChanging: boolean) => void;
  onAmountChangeError?: (error: PrimerClientError) => void;

  threeDSecure?: ThreeDSVerificationOptions;
  card?: CheckoutCardOptions;
  paypal?: Omit<PayPalOptions, 'container'>;
  googlePay?: Omit<GooglePayOptions, 'container'>;
  applePay?: Omit<ApplePayOptions, 'container'>;
  klarna?: KlarnaOptions;
  directDebit?: DirectDebitOptions;
  submitButton?: SubmitButtonOptions;
  processingIndicator?: ProcessingIndicatorOptions;
  style?: CheckoutStyle;
  errorMessage?: ErrorMessageOptions;
  successScreen?: SuccessScreenOptions;
  form?: FormOptions;
  customerDetails?: CustomerDetails;
  businessDetails?: BusinessDetails;
  orderDetails?: OrderDetails;
}

type FieldMetadata = {
  // A validation error
  error?: string;
  // Whether or not the input contains valid information
  valid: boolean;
  // Whether or not the input is focussed
  active: boolean;
  // Whether or not the input's value has been changed
  dirty: boolean;
  // Whether or not the customer has focussed the field
  touched: boolean;
  // Whether or not the form has been submitted
  submitted: boolean;
};

type CreditCardFieldConfig = {
  // A DOM selector specifying where to render the input
  container: string;
  // Whether to append or prepend the input to the container element
  placement?: 'append' | 'prepend';
  // A placeholder to display when the input is empty
  placeholder?: string;
  // A callback which receives input metadata
  onChange: (data: { meta: FieldMetadata }) => void;
};
export interface RenderOptions {
  uxFlow: null;
  locale?: string;
  countryCode?: Alpha2CountryCode;
  /**
   * @deprecated Use `orderDetails` instead
   */
  purchaseInfo?: PurchaseInformation;
  customerDetails?: CustomerDetails;
  businessDetails?: BusinessDetails;
  orderDetails?: OrderDetails;
  onTokenizeStart?: () => void;
  onTokenizeSuccess: (data: PaymentMethodToken) => void;
  onTokenizeError?: (error: PrimerClientError) => void;
  onTokenizeProgress?: (evt: { type: string }) => void;
  card?: {
    // The name of the cardholder
    cardholderName?: MaybeThunk<string>;

    // A callback for form metadata
    onChange?: (state: FormState) => void;

    // A callback for extra card information (brand etc)
    onCardMetadata?: (meta: CardMetadata) => void;

    // Whether or not the form is disabled - this will prevent tokenization if it returns true
    disabled?: () => boolean;

    // A stylesheet to inject into each input element
    // For more information see the guide on styling
    css?: string;

    // A DOM selector for a button which will begin the tokenization
    submitButton: string;

    // Credit card field configuration
    fields: {
      // Card number field configuration
      cardNumber: CreditCardFieldConfig;
      // Expiry Date field configuration
      expiryDate: CreditCardFieldConfig;
      // CVV field configuration
      cvv: CreditCardFieldConfig;
    };
  };

  applePay?: { container: string };
  googlePay?: { container: string };
  paypal?: { container: string };
}

export interface PrimerCheckout {
  teardown(): void;
  validate(): Promise<Validation>;
  tokenize(): Promise<void>;
  /**
   * @deprecated Use `setOrderDetails` instead
   */
  setPurchaseInfo(purchaseInfo: PurchaseInformation): Promise<void>;
  setCustomerDetails(customerDetails: CustomerDetails | null): Promise<void>;
  setBusinessDetails(businessDetails: BusinessDetails | null): Promise<void>;
  setOrderDetails(
    orderDetails: OrderDetails | null,
    orderDetailsInitiator: string | null,
  ): Promise<void>;
}

export interface PrimerSinglePaymentMethodCheckout {
  validate(): Promise<Validation>;
  tokenize(): Promise<void>;
}

export interface PrimerVaultManager {
  teardown(): void;
  validate(): Promise<Validation>;
  tokenize(): Promise<void>;
}

export interface CardDetails {
  last4Digits: string;
  cardNumber: string;
  name: string;
  icon: string;
  network: string;
}

export interface PayPalDetails {
  icon: string;
  email?: string;
}

export interface VaultListItem {
  id: string;
  type: PaymentInstrumentType;
  details: CardDetails | PayPalDetails;
}

export interface RemotePaymentMethodConfiguration<T> {
  id: string;
  options: T;
}
